import { DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useEffect, useRef } from 'react';

import { useTableContext } from './Table.context';
import { BaseItem, ColumnDefinition, TableProps, UseVerticalIntersectionParams } from './Table.types';

export const useTableReorder = <Item extends BaseItem>(
    params: Pick<TableProps<Item>, 'reorderable' | 'columns' | 'data' | 'onReorderColumns' | 'onReorderRows'>
) => {
    const { reorderable, columns, data, onReorderColumns, onReorderRows } = params;

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, { activationConstraint: { distance: 10 } })
    );

    const handleReorderColumn = useCallback(
        (active: DragEndEvent['active'], over: DragEndEvent['over']) => {
            if (!onReorderColumns) {
                return;
            }

            if (over && active.id !== over?.id) {
                const indexPrev = columns.findIndex((col) => col.id === active.id);
                const indexNext = columns.findIndex((col) => col.id === over.id);

                const columnsReordered = arrayMove(columns, indexPrev, indexNext);

                onReorderColumns(columnsReordered, indexPrev, indexNext, columns);
            }
        },
        [columns, onReorderColumns]
    );

    const handleReorderRow = useCallback(
        (active: DragEndEvent['active'], over: DragEndEvent['over']) => {
            if (!onReorderRows) {
                return;
            }

            if (over && active.id !== over?.id) {
                const indexPrev = data.findIndex((col) => col.id === active.id);
                const indexNext = data.findIndex((col) => col.id === over.id);

                const dataReordered = arrayMove(data, indexPrev, indexNext);

                onReorderRows(dataReordered, indexPrev, indexNext, data);
            }
        },
        [data, onReorderRows]
    );

    const handleReordered = useCallback(
        (event: DragEndEvent) => {
            if (!reorderable) {
                return;
            }

            const { active, over } = event;

            switch (active.data.current?.type) {
                case 'column':
                    handleReorderColumn(active, over);
                    break;

                case 'row':
                    handleReorderRow(active, over);
                    break;
            }
        },
        [handleReorderColumn, handleReorderRow, reorderable]
    );

    return {
        sensors,
        handleReordered,
    };
};

const useVerticalIntersection = (params: UseVerticalIntersectionParams) => {
    const { enabled, onIntersection } = params;

    const cellRef = useRef<HTMLTableCellElement | null>(null);

    const setCellRef = useCallback((node: HTMLTableCellElement | null) => {
        cellRef.current = node;
    }, []);

    useEffect(() => {
        const element = cellRef.current;

        if (!enabled || !element) {
            return;
        }

        const observer = new IntersectionObserver(
            ([e]) => {
                onIntersection(e.intersectionRatio < 1);
            },
            {
                threshold: 1,
                root: document, // for correct works rootMargin
                rootMargin: '5000px 0px', // to detect an intersection with a vertical line only
            }
        );

        observer.observe(element);

        return () => {
            observer.unobserve(element);
        };
    }, [enabled, onIntersection]);

    return { setCellRef };
};

export const useStickyShadow = <Item extends BaseItem>(sticky: ColumnDefinition<Item>['sticky']) => {
    const { isLeftStickyShadow, isRightStickyShadow, setIsLeftStickyShadow, setIsRightStickyShadow } =
        useTableContext();

    const withStickyShadow = sticky === 'left' ? isLeftStickyShadow : isRightStickyShadow;
    const setWithStickyShadow = sticky === 'left' ? setIsLeftStickyShadow : setIsRightStickyShadow;

    const { setCellRef } = useVerticalIntersection({
        enabled: Boolean(sticky),
        onIntersection: setWithStickyShadow,
    });

    return { setCellRef, withStickyShadow };
};

export const useStickyRowMenu = (enabled: boolean) => {
    const { isRowMenuStickyActive, setIsRowMenuStickyActive } = useTableContext();

    const { setCellRef } = useVerticalIntersection({
        enabled,
        onIntersection: setIsRowMenuStickyActive,
    });

    return { setCellRef, isRowMenuStickyActive };
};
